'use client';

import { useState } from 'react';

interface CouponInputProps {
    onApply: (couponCode: string) => Promise<void>;
    onRemove: () => void;
    appliedCoupon: string | null;
    couponReason?: string;
    isValidating: boolean;
    error?: string | null;
    isDarkMode?: boolean;
}

/**
 * Coupon input component with toggle, validation, and applied badge
 */
export function CouponInput({
    onApply,
    onRemove,
    appliedCoupon,
    couponReason,
    isValidating,
    error,
    isDarkMode = true,
}: CouponInputProps) {
    const [isExpanded, setIsExpanded] = useState(!!appliedCoupon);
    const [inputValue, setInputValue] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim() || isValidating) return;

        await onApply(inputValue.trim().toUpperCase());
    };

    const handleRemove = () => {
        setInputValue('');
        setIsExpanded(false); // Collapse back to "Enter coupon code" link
        onRemove();
    };

    // If coupon is applied, show the badge
    if (appliedCoupon) {
        return (
            <div className="space-y-2">
                <div
                    className={`flex items-center justify-between rounded-lg px-4 py-3 ${
                        isDarkMode
                            ? 'bg-purple-900/20 border border-purple-800'
                            : 'bg-purple-50'
                    }`}
                >
                    <div className="flex items-center space-x-2">
                        <svg
                            className={`w-5 h-5 ${
                                isDarkMode
                                    ? 'text-purple-400'
                                    : 'text-purple-600'
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                            />
                        </svg>
                        <div>
                            <p
                                className={`text-sm font-medium ${
                                    isDarkMode
                                        ? 'text-purple-300'
                                        : 'text-purple-700'
                                }`}
                            >
                                Coupon applied: {appliedCoupon}
                            </p>
                            {couponReason && (
                                <p
                                    className={`text-xs ${
                                        isDarkMode
                                            ? 'text-purple-400'
                                            : 'text-purple-600'
                                    }`}
                                >
                                    {couponReason}
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={handleRemove}
                        className={`transition-colors p-1 ${
                            isDarkMode
                                ? 'text-purple-400 hover:text-purple-300'
                                : 'text-purple-600 hover:text-purple-800'
                        }`}
                        aria-label="Remove coupon"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>
            </div>
        );
    }

    // Toggle link to expand input
    if (!isExpanded) {
        return (
            <button
                onClick={() => setIsExpanded(true)}
                className={`text-sm transition-colors flex items-center space-x-1 ${
                    isDarkMode
                        ? 'text-purple-400 hover:text-purple-300'
                        : 'text-purple-600 hover:text-purple-800'
                }`}
            >
                <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                    />
                </svg>
                <span>Enter coupon code</span>
            </button>
        );
    }

    // Expanded coupon input form
    return (
        <div className="space-y-2">
            <form onSubmit={handleSubmit} className="flex space-x-2">
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) =>
                        setInputValue(e.target.value.toUpperCase())
                    }
                    placeholder="Enter coupon code"
                    className={`flex-1 px-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                        error
                            ? isDarkMode
                                ? 'border-red-700 bg-red-900/20 text-white'
                                : 'border-red-300 bg-red-50 text-gray-900'
                            : isDarkMode
                            ? 'border-gray-600 bg-gray-800 text-white placeholder-gray-500'
                            : 'border-gray-300 bg-white text-gray-900'
                    }`}
                    disabled={isValidating}
                    autoComplete="off"
                    autoCapitalize="characters"
                />
                <button
                    type="submit"
                    disabled={!inputValue.trim() || isValidating}
                    className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                >
                    {isValidating ? (
                        <>
                            <svg
                                className="animate-spin h-4 w-4 text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                            >
                                <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                ></circle>
                                <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                            </svg>
                            <span>Validating...</span>
                        </>
                    ) : (
                        <span>Apply</span>
                    )}
                </button>
            </form>

            {/* Error message */}
            {error && (
                <p
                    className={`text-sm flex items-center space-x-1 ${
                        isDarkMode ? 'text-red-400' : 'text-red-600'
                    }`}
                >
                    <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <span>{error}</span>
                </p>
            )}

            {/* Collapse button */}
            <button
                onClick={() => {
                    setIsExpanded(false);
                    setInputValue('');
                }}
                className={`text-xs transition-colors ${
                    isDarkMode
                        ? 'text-gray-400 hover:text-gray-300'
                        : 'text-gray-500 hover:text-gray-700'
                }`}
            >
                Cancel
            </button>
        </div>
    );
}

export default CouponInput;
